import "./App.css";
import { createContext, useEffect, useRef, useState } from "react";
import { Button, Grid, Modal, Box, Typography } from "@mui/material";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
// import AudioManager from "./utils/AudioManager";
import { useSoundsStore } from "./@shared/store/useSoundsStore";
import { storage, auth } from "../src/@shared/firebase/config"; // your firebase storage instance
import { signOut } from "firebase/auth";
import { ref, getDownloadURL, listAll } from "firebase/storage";
import { useAuthStore } from "./@shared/store/useAuthStore";
import { ThemeProvider, createTheme } from "@mui/material";
import { theme as Theme } from "./Theme";
import Toast from "./components/Toast";
import MenuBar from "./components/MenuBar";
import HomeScreen from "./screens/HomeScreen";
import SplashScreen from "./screens/SplashScreen";
import CardManager from "./components/CardManager";
import { useCardStore } from "./@shared/store/useCardsStore";
import MyCardsScreen from "./screens/MyCardsScreen";
import MyDecksScreen from "./screens/MyDecksScreen";
import DeckBuilder from "./screens/DeckBuilder";
import GameBoardScreen from "./screens/GameBoardScreen";
import Dashboard from "./screens/Dashboard";
import LobbyScreen from "./screens/LobbyScreen";
import StoreScreen from "./screens/StoreScreen";
import FightAnimation from "./components/gamePlay/FightAnimation";
import SlapCardAnimation from "./components/gamePlay/SlapCardAnimation";
import TradeCardAnimation from "./components/gamePlay/TradeCardAnimation";
import GradientExamples from "./GradientExamples";
import Test from "./screens/Test";
import WorldScreen from "./screens/WorldScreen";
import AdminScreen from "./screens/AdminScreen";
import MusicController from "./components/MusicController";

import playerHit from "./@shared/assets/sounds/anime-hit-sfx.mp3";
import doorTransition from "./@shared/assets/sounds/door-transition.mp3";
import buff1 from "./@shared/assets/sounds/sfx-buff.mp3";
import cardSlapPackOpen from "./@shared/assets/sounds/sfx-cards-woosh.mp3";
import cardSwooshIn from "./@shared/assets/sounds/sfx-swoosh-in.mp3";
import cardSwooshOut from "./@shared/assets/sounds/sfx-swoosh-out.mp3";
import buttonPress from "./@shared/assets/sounds/sfx-menu-button.mp3";
import buttonPressNoGood from "./@shared/assets/sounds/sfx-menu-button-cancel.mp3";
import liveChat from "./@shared/assets/sounds/sfx-live-chat.mp3";
import AudioManager from "./@shared/utils/AudioManager";
import OnboardingScreen from "./screens/OnboardingScreen";
import TransitionOverlay from "./components/TransitionOverlay";
import PlayerDashboard from "./screens/PlayerDashboard";
import LoadingScreen from "./components/LoadingScreen";

export let MainContext = createContext({});

function App() {
  const { user, userData, loading, initAuth } = useAuthStore();
  const { fetchCards } = useCardStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [info, setInfo] = useState(false);
  const [message, setMessage] = useState("!");
  const [deviceType, setDeviceType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [partyId, setPartyId] = useState(null);
  const [isHost, setIsHost] = useState(null);
  const [card, setCard] = useState(null);
  const [actionTaken, setActionTaken] = useState(false);
  const [screenLoading, setScreenLoading] = useState(false);
  const [gameScreenLoading, setGameScreenLoading] = useState(false);

  //! RESPONSIBLE FOR LOCK MODAL
  const shouldShowLockModal =
    userData &&
    !userData.roles?.includes("admin") &&
    location.pathname !== "/onboarding";

  // A map of your screens → folder paths in storage
  const MUSIC_FOLDERS = {
    dashboard: "soundtracks/dashboard",
    gameboard: "soundtracks/gameboard",
    lobby: "soundtracks/lobby",
    mycards: "soundtracks/mycards",
  };

  const setSoundBank = useSoundsStore((s) => s.setSfxBankAndReady);
  const setMusicBank = useSoundsStore((s) => s.setMusicBankAndReady);
  const { musicBank, sfxBank } = useSoundsStore();

  const musicVolume = useSoundsStore((s) => s.musicVolume);
  const unlockedRef = useRef(false);

  const handleForceSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (err) {
      console.error("Forced sign-out failed:", err.message);
    }
  };

  useEffect(() => {
    const unlockAudio = () => {
      if (unlockedRef.current) return;
      // Simply create & play a silent buffer to unlock the audio context:
      const a = new Audio();
      a.volume = 0;
      a.play()
        .catch(() => {})
        .finally(() => {
          a.pause();
          unlockedRef.current = true;

          // 🔁 Retry playing music that was blocked earlier

          // const pending = AudioManager.pendingMusic;
          // if (pending) {
          //   console.log("🔁 Retrying pending music play...");
          //   AudioManager.playMusic(pending.url, pending.volume);
          // }

          // const pendingPlaylist = AudioManager.pendingPlaylist;
          // if (pendingPlaylist) {
          //   console.log("🔁 Retrying pending playlist...");
          //   AudioManager.playPlaylist(
          //     pendingPlaylist.urls,
          //     pendingPlaylist.volume
          //   );
          // }
        });
      document.removeEventListener("click", unlockAudio);
    };
    document.addEventListener("click", unlockAudio);
    return () => document.removeEventListener("click", unlockAudio);
  }, []);

  useEffect(() => {
    async function preloadAllAudio() {
      // 3a) Preload SFX
      await Promise.all([
        AudioManager.loadSFX("playerHit", playerHit),
        AudioManager.loadSFX("doorTransition", doorTransition),
        AudioManager.loadSFX("buff1", buff1),
        AudioManager.loadSFX("cardSlapPackOpen", cardSlapPackOpen),
        AudioManager.loadSFX("cardSwooshIn", cardSwooshIn),
        AudioManager.loadSFX("cardSwooshOut", cardSwooshOut),
        AudioManager.loadSFX("buttonPress", buttonPress),
        AudioManager.loadSFX("buttonPressNoGood", buttonPressNoGood),
        AudioManager.loadSFX("liveChat", liveChat),
      ]);

      // ✅ Save it to your Zustand store if you need access elsewhere
      setSoundBank(AudioManager.sfxBank);

      // 3b) Preload Music by screen
      const musicList = {};
      for (let screen of Object.keys(MUSIC_FOLDERS)) {
        const folderRef = ref(storage, MUSIC_FOLDERS[screen]);
        const list = await listAll(folderRef);
        const urls = await Promise.all(
          list.items.map((itemRef) => getDownloadURL(itemRef))
        );
        musicList[screen] = urls;
      }
      // hand your store the mapping
      setMusicBank(musicList);
    }

    preloadAllAudio().catch(console.error);
  }, [setSoundBank, setMusicBank]);

  useEffect(() => {
    if (userData) {
      console.log(userData);
      setCard(userData.cards[0]);
      return;
    }
    initAuth();
    fetchCards();
  }, []);

  useEffect(() => {
    if (isLoading) {
      setTimeout(() => {
        setIsLoading(false);
      }, 3000);
    }
  }, []);

  //useEffect to set device type
  useEffect(() => {
    if (window.innerWidth < 600) {
      setDeviceType("mobile");
    } else setDeviceType("desktop");
  }, []);

  useEffect(() => {
    if (sfxBank) {
      console.log("This is the list of songs.. or song obj? =>", sfxBank);
    }
  }, [sfxBank]);

  const values = {
    error,
    setError,
    success,
    setSuccess,
    info,
    setInfo,
    message,
    setMessage,
    Theme,
    navigate,
    deviceType,
    partyId,
    setPartyId,
    isHost,
    setIsHost,
    screenLoading,
    setScreenLoading,
    gameScreenLoading,
    setGameScreenLoading,
  };

  return (
    <MainContext.Provider value={values}>
      <ThemeProvider theme={createTheme(Theme)}>
        <Grid
          item
          container
          xs={12}
          style={{
            height: "100dvh",
            width: "100dvw",
            overflowX: "hidden",
            justifyContent: "center",
          }}
        >
          <MusicController />

          {/* <Toast /> */}
          {isLoading ? (
            <SplashScreen />
          ) : (
            <Grid item container xs={12} style={{ height: "100%" }}>
              {/* <Grid item container xs={12} style={{ height: 64, zIndex: 2 }}>
                <MenuBar navigate={navigate} />
              </Grid> */}
              <Grid
                item
                container
                xs={12}
                style={{ height: "100dvh", zIndex: 1 }}
                // style={{ height: "calc(100dvh - 64px)", zIndex: 1 }}
              >
                <Routes>
                  <Route path="/" element={<HomeScreen />} />
                  <Route path="/dashboard" element={<PlayerDashboard />} />
                  <Route path="/world" element={<WorldScreen />} />
                  <Route path="/auth" element={<HomeScreen isAuth={true} />} />
                  <Route path="/cardmanager" element={<CardManager />} />
                  <Route path="/mycards" element={<MyCardsScreen />} />
                  <Route path="/mydecks" element={<MyDecksScreen />} />
                  <Route path="/deckbuilder" element={<DeckBuilder />} />
                  <Route
                    exact
                    path="/gameboard/:roomId/match"
                    element={<GameBoardScreen />}
                  />
                  <Route path="/lobby" element={<LobbyScreen />} />
                  <Route path="/store" element={<StoreScreen />} />
                  <Route
                    path="/store/success/:productType"
                    element={<StoreScreen />}
                  />
                  <Route path="/test" element={<Test />} />
                  <Route path="/admin" element={<AdminScreen />} />
                  <Route path="/onboarding" element={<OnboardingScreen />} />

                  <Route
                    path="/test"
                    element={
                      <Grid
                        item
                        container
                        xs={12}
                        style={{
                          height: "100%",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <GradientExamples />
                        {userData && (
                          <Button
                            onClick={() => {
                              setActionTaken(true);
                            }}
                          >
                            Animate
                          </Button>
                        )}
                      </Grid>
                    }
                  />
                </Routes>

                {/* ✅ Always render this so it's available for screen transitions */}
                <TransitionOverlay />
                <LoadingScreen />
                {/* GLOBAL MODAL THAT CAN BE USED FOR IMPORTANT MESSAGES */}
                <Modal open={false}>
                  <Box
                    sx={{
                      backgroundColor: "#111",
                      color: "#fff",
                      maxWidth: 500,
                      mx: "auto",
                      my: "20vh",
                      p: 4,
                      borderRadius: 3,
                      boxShadow: 24,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
                      🚀 InfiNFT Royale: Pre-Season is Heating Up!
                    </Typography>

                    <Typography sx={{ fontSize: 15, mb: 2 }}>
                      We're gearing up for the biggest launch in NFT card gaming
                      — and you're here early. That means more ways to stand out
                      and leave your mark.
                    </Typography>

                    <Typography sx={{ fontSize: 15, mb: 2 }}>
                      Make sure you’re ready. Stay updated. Get your cards in.
                      Support the launch. Let’s build something legendary —
                      together.
                    </Typography>

                    <Typography sx={{ fontSize: 15, mb: 3 }}>
                      If you're just exploring, that's cool too. Come back when
                      you're ready to join the fight.
                    </Typography>

                    <Button
                      variant="contained"
                      onClick={handleForceSignOut}
                      sx={{
                        bgcolor: "#6f52ed",
                        "&:hover": { bgcolor: "#5a3ed3" },
                        fontWeight: 700,
                        mt: 2,
                      }}
                    >
                      Confirm & Sign Out
                    </Button>
                  </Box>
                </Modal>
              </Grid>
            </Grid>
          )}
        </Grid>
      </ThemeProvider>
    </MainContext.Provider>
  );
}

export default App;
